import React, { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';

export function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    // Check for saved theme preference or system preference
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';

    const initialTheme = savedTheme || systemTheme;
    setTheme(initialTheme);

    // Apply theme to document
    if (initialTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);

    // Save to localStorage
    localStorage.setItem('theme', newTheme);

    // Apply to document
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cn(
        "relative flex items-center justify-center",
        "h-8 w-14 flex-shrink-0",
        "rounded-full",
        "bg-slate-200 dark:bg-slate-700",
        "border border-slate-300 dark:border-slate-600",
        "transition-colors duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
      )}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {/* Sliding thumb */}
      <span
        className={cn(
          "absolute top-0.5 left-0.5",
          "flex items-center justify-center",
          "h-6 w-6 rounded-full",
          "bg-white dark:bg-slate-900",
          "shadow-sm",
          "transition-transform duration-300 ease-out",
          theme === 'dark' && "translate-x-6"
        )}
      >
        {/* Sun icon */}
        <Sun
          className={cn(
            "absolute",
            "!h-3.5 !w-3.5",
            "text-amber-500",
            "transition-all duration-300",
            theme === 'light' ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-0"
          )}
        />
        {/* Moon icon */}
        <Moon
          className={cn(
            "absolute",
            "!h-3.5 !w-3.5",
            "text-slate-400 dark:text-slate-300",
            "transition-all duration-300",
            theme === 'dark' ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"
          )}
        />
      </span>
    </button>
  );
}
